import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { admin as adminApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DollarSign, Users, Mail, ShoppingCart, Repeat, TrendingUp, Eye, Download, Globe, Gift, MousePointerClick } from "lucide-react";
import AdminBlog from "@/components/AdminBlog";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const SOURCE_COLORS = {
  direct: "#4F46E5",
  organic: "#10B981",
  social: "#EC4899",
  referral: "#F59E0B",
  paid: "#8B5CF6",
  email: "#06B6D4",
};
const SOURCE_LABELS = {
  direct: "Directo",
  organic: "Búsqueda orgánica",
  social: "Redes sociales",
  referral: "Referidos",
  paid: "Pago / Ads",
  email: "Email",
};

const Stat = ({ icon: Icon, label, value, prefix = "" }) => (
  <div className="bg-white border border-zinc-200 p-6">
    <Icon className="w-5 h-5 text-[#4F46E5] mb-3" strokeWidth={1.5} />
    <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-1">{label}</div>
    <div className="font-display font-black text-3xl tracking-tight">
      {prefix}
      {value}
    </div>
  </div>
);

const Admin = () => {
  const { user, loading } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [txs, setTxs] = useState([]);
  const [pp, setPp] = useState(null);
  const [ppMode, setPpMode] = useState("day");
  const [subs, setSubs] = useState([]);
  const [traffic, setTraffic] = useState(null);
  const [refs, setRefs] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (user?.role === "admin") {
      adminApi.metrics().then(setMetrics).catch(() => {});
      adminApi.leads().then(setLeads).catch(() => {});
      adminApi.users().then(setUsers).catch(() => {});
      adminApi.transactions().then(setTxs).catch(() => {});
      adminApi.paypalStats().then(setPp).catch(() => {});
      adminApi.subscribers().then(setSubs).catch(() => {});
      adminApi.traffic().then(setTraffic).catch(() => {});
      adminApi.referrals().then(setRefs).catch(() => {});
    }
  }, [user]);

  const exportSubscribers = () => {
    const rows = [["Email", "Nombre", "Origen", "Estado", "Fecha"]].concat(
      subs.map((s) => [s.email, s.name || "", s.source || "", s.status || "", s.created_at || ""])
    );
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "suscriptores-kursos.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const tabs = [
    { id: "overview", label: "Resumen" },
    { id: "blog", label: "Blog & IA" },
    { id: "traffic", label: "Tráfico" },
    { id: "subscribers", label: `Suscriptores (${subs.length})` },
    { id: "referrals", label: "Referidos" },
    { id: "paypal", label: "Pagos PayPal" },
    { id: "leads", label: `Leads (${leads.length})` },
    { id: "users", label: `Estudiantes (${users.length})` },
    { id: "txs", label: `Transacciones (${txs.length})` },
  ];

  return (
    <main data-testid="admin-page" className="bg-zinc-50 min-h-[80vh]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF3B30] mb-3">Admin</p>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-10">
          Panel de control
        </h1>

        <div className="border-b border-zinc-200 flex gap-6 mb-10 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`pb-3 text-sm font-semibold border-b-2 transition whitespace-nowrap ${
                tab === t.id
                  ? "border-[#4F46E5] text-[#4F46E5]"
                  : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && metrics && (
          <div className="space-y-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat icon={DollarSign} label="Ingresos totales" value={metrics.total_revenue} prefix="$" />
              <Stat icon={ShoppingCart} label="Ventas" value={metrics.total_sales} />
              <Stat icon={Users} label="Estudiantes" value={metrics.total_users} />
              <Stat icon={Mail} label="Leads" value={metrics.total_leads} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white border border-zinc-200 p-6">
                <h3 className="font-display font-bold text-lg mb-6">Ingresos por curso</h3>
                {metrics.revenue_by_course.length === 0 ? (
                  <p className="text-sm text-zinc-500">Sin datos aún.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={metrics.revenue_by_course}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                      <XAxis dataKey="course" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="revenue" fill="#4F46E5" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

              <div className="bg-white border border-zinc-200 p-6">
                <h3 className="font-display font-bold text-lg mb-6">Ingresos por día</h3>
                {metrics.sales_by_day.length === 0 ? (
                  <p className="text-sm text-zinc-500">Sin datos aún.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={metrics.sales_by_day}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="revenue" stroke="#4F46E5" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "blog" && <AdminBlog />}

        {tab === "traffic" && traffic && (
          <div className="space-y-8" data-testid="admin-traffic-panel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat icon={Eye} label="Visitas totales" value={traffic.total_visits} />
              <Stat icon={Globe} label="Eventos rastreados" value={traffic.tracked_events} />
              <Stat icon={Mail} label="Suscriptores" value={traffic.total_subscribers} />
              <Stat icon={MousePointerClick} label="Fuentes activas" value={traffic.by_source.length} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white border border-zinc-200 p-6">
                <h3 className="font-display font-bold text-lg mb-6">Visitas por día</h3>
                {traffic.by_day.length === 0 ? (
                  <p className="text-sm text-zinc-500">Sin datos aún.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={traffic.by_day}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="visits" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>

              <div className="bg-white border border-zinc-200 p-6">
                <h3 className="font-display font-bold text-lg mb-6">Origen del tráfico</h3>
                {traffic.by_source.length === 0 ? (
                  <p className="text-sm text-zinc-500">Sin datos aún. Añade ?utm_source o comparte referidos.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={traffic.by_source.map((s) => ({ name: SOURCE_LABELS[s.source] || s.source, value: s.visits, key: s.source }))}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        label
                      >
                        {traffic.by_source.map((s) => (
                          <Cell key={s.source} fill={SOURCE_COLORS[s.source] || "#94A3B8"} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white border border-zinc-200 p-6">
                <h3 className="font-display font-bold text-lg mb-4">Sitios que te envían tráfico</h3>
                {traffic.top_referrers.length === 0 ? (
                  <p className="text-sm text-zinc-500">Sin referrers registrados aún.</p>
                ) : (
                  <ul className="divide-y divide-zinc-100">
                    {traffic.top_referrers.map((r) => (
                      <li key={r.domain} className="flex justify-between py-2.5 text-sm" data-testid={`referrer-${r.domain}`}>
                        <span className="font-mono text-zinc-700">{r.domain}</span>
                        <span className="font-semibold">{r.visits}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="bg-white border border-zinc-200 p-6">
                <h3 className="font-display font-bold text-lg mb-4">Campañas (UTM)</h3>
                {traffic.campaigns.length === 0 ? (
                  <p className="text-sm text-zinc-500">Usa ?utm_campaign=... en tus enlaces para medir campañas.</p>
                ) : (
                  <ul className="divide-y divide-zinc-100">
                    {traffic.campaigns.map((c) => (
                      <li key={c.campaign} className="flex justify-between py-2.5 text-sm">
                        <span className="text-zinc-700">{c.campaign}</span>
                        <span className="font-semibold">{c.visits}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "subscribers" && (
          <div className="space-y-4" data-testid="admin-subscribers-panel">
            <div className="flex items-center justify-between">
              <p className="text-sm text-zinc-600">
                {subs.length} suscriptores de boletín. Expórtalos para tu herramienta de email marketing.
              </p>
              <button
                onClick={exportSubscribers}
                disabled={subs.length === 0}
                data-testid="export-subscribers-btn"
                className="inline-flex items-center gap-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50"
              >
                <Download className="w-4 h-4" /> Exportar CSV
              </button>
            </div>
            <div className="bg-white border border-zinc-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="text-left p-4 font-semibold">Correo</th>
                    <th className="text-left p-4 font-semibold">Nombre</th>
                    <th className="text-left p-4 font-semibold">Origen</th>
                    <th className="text-left p-4 font-semibold">Estado</th>
                    <th className="text-left p-4 font-semibold">Fecha</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-200">
                  {subs.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="p-8 text-center text-zinc-500">Sin suscriptores aún.</td>
                    </tr>
                  ) : (
                    subs.map((s) => (
                      <tr key={s.id} data-testid={`subscriber-row-${s.id}`} className="hover:bg-zinc-50">
                        <td className="p-4">{s.email}</td>
                        <td className="p-4">{s.name || "—"}</td>
                        <td className="p-4">
                          <span className="text-xs px-2 py-1 bg-zinc-100">{s.source || "—"}</span>
                        </td>
                        <td className="p-4">
                          <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-800">{s.status}</span>
                        </td>
                        <td className="p-4 text-zinc-500 font-mono text-xs">{(s.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "referrals" && refs && (
          <div className="space-y-6" data-testid="admin-referrals-panel">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Stat icon={MousePointerClick} label="Clics de referidos" value={refs.total_clicks} />
              <Stat icon={Users} label="Registros por referido" value={refs.total_referred_signups} />
              <Stat icon={Gift} label="Embajadores activos" value={refs.leaderboard.length} />
            </div>
            <div className="bg-white border border-zinc-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="text-left p-4 font-semibold">#</th>
                    <th className="text-left p-4 font-semibold">Embajador</th>
                    <th className="text-left p-4 font-semibold">Código</th>
                    <th className="text-left p-4 font-semibold">Clics</th>
                    <th className="text-left p-4 font-semibold">Registros</th>
                    <th className="text-left p-4 font-semibold">Compradores</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-200">
                  {refs.leaderboard.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="p-8 text-center text-zinc-500">
                        Aún no hay actividad de referidos. Cada estudiante tiene su enlace en el dashboard.
                      </td>
                    </tr>
                  ) : (
                    refs.leaderboard.map((r, i) => (
                      <tr key={r.code} data-testid={`referral-row-${r.code}`} className="hover:bg-zinc-50">
                        <td className="p-4 font-display font-bold">{i + 1}</td>
                        <td className="p-4">
                          <div className="font-semibold">{r.name}</div>
                          <div className="text-xs text-zinc-500">{r.email}</div>
                        </td>
                        <td className="p-4 font-mono text-xs">{r.code}</td>
                        <td className="p-4">{r.clicks}</td>
                        <td className="p-4 font-semibold">{r.signups}</td>
                        <td className="p-4 text-[#10B981] font-semibold">{r.buyers}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "paypal" && pp && (
          <div className="space-y-8" data-testid="admin-paypal-panel">
            <div className="bg-[#FFC439]/20 border border-[#FFC439] p-4 text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span>
                💰 Los pagos llegan directamente a tu cuenta PayPal:{" "}
                <span className="font-semibold">{pp.merchant_email}</span>
              </span>
              <span
                data-testid="webhook-status-badge"
                className={`text-xs px-2 py-1 font-semibold whitespace-nowrap ${
                  pp.webhook_configured
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-zinc-200 text-zinc-600"
                }`}
              >
                {pp.webhook_configured
                  ? "✓ Webhook activo · renovaciones y cancelaciones automáticas"
                  : "Webhook pendiente de configurar"}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Stat icon={DollarSign} label="Ingresos PayPal" value={pp.total_revenue} prefix="$" />
              <Stat icon={ShoppingCart} label="Pagos completados" value={pp.total_paid} />
              <Stat icon={Repeat} label="Suscripciones activas" value={pp.active_subscriptions} />
              <Stat icon={TrendingUp} label="MRR (mensual)" value={pp.mrr} prefix="$" />
              <Stat icon={Eye} label="Conversión" value={`${pp.conversion_rate}%`} />
            </div>

            <div className="bg-white border border-zinc-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display font-bold text-lg">
                  Ingresos PayPal por {ppMode === "day" ? "día" : "mes"}
                </h3>
                <div className="flex border border-zinc-200">
                  {["day", "month"].map((m) => (
                    <button
                      key={m}
                      onClick={() => setPpMode(m)}
                      data-testid={`paypal-chart-${m}`}
                      className={`px-4 py-1.5 text-xs font-semibold transition ${
                        ppMode === m ? "bg-[#4F46E5] text-white" : "text-zinc-500 hover:text-zinc-900"
                      }`}
                    >
                      {m === "day" ? "Por día" : "Por mes"}
                    </button>
                  ))}
                </div>
              </div>
              {(ppMode === "day" ? pp.revenue_by_day : pp.revenue_by_month).length === 0 ? (
                <p className="text-sm text-zinc-500">
                  Sin pagos PayPal aún. Comparte tu sitio para atraer tráfico.
                </p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={ppMode === "day" ? pp.revenue_by_day : pp.revenue_by_month}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis dataKey={ppMode === "day" ? "date" : "month"} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="revenue" fill="#4F46E5" />
                  </BarChart>
                </ResponsiveContainer>
              )}
              <p className="text-xs text-zinc-500 mt-4">
                Visitas totales registradas: <span className="font-semibold">{pp.total_visits}</span>{" "}
                · Pagos pendientes/abandonados: <span className="font-semibold">{pp.total_pending}</span>
              </p>
            </div>

            <div className="bg-white border border-zinc-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="text-left p-4 font-semibold">Tipo</th>
                    <th className="text-left p-4 font-semibold">Producto</th>
                    <th className="text-left p-4 font-semibold">Comprador</th>
                    <th className="text-left p-4 font-semibold">Monto</th>
                    <th className="text-left p-4 font-semibold">Estado</th>
                    <th className="text-left p-4 font-semibold">Fecha</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-200">
                  {pp.transactions.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="p-8 text-center text-zinc-500">
                        Sin transacciones PayPal aún.
                      </td>
                    </tr>
                  ) : (
                    pp.transactions.map((t) => (
                      <tr key={t.id} data-testid={`paypal-tx-${t.id}`} className="hover:bg-zinc-50">
                        <td className="p-4">
                          <span className={`text-xs px-2 py-1 ${t.type === "subscription" ? "bg-[#FFC439]/30 text-amber-900" : "bg-blue-50 text-blue-800"}`}>
                            {t.type === "subscription" ? "Suscripción" : "Curso"}
                          </span>
                        </td>
                        <td className="p-4">{t.course_title}</td>
                        <td className="p-4">{t.user_email}</td>
                        <td className="p-4 font-semibold">${t.amount} {t.currency?.toUpperCase()}</td>
                        <td className="p-4">
                          <span className={`text-xs px-2 py-1 ${t.payment_status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100"}`}>
                            {t.payment_status}
                          </span>
                        </td>
                        <td className="p-4 text-zinc-500 font-mono text-xs">{(t.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div className="bg-white border border-zinc-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="text-left p-4 font-semibold">Nombre</th>
                  <th className="text-left p-4 font-semibold">Correo</th>
                  <th className="text-left p-4 font-semibold">Interés</th>
                  <th className="text-left p-4 font-semibold">Fecha</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="p-8 text-center text-zinc-500">Sin leads aún.</td>
                  </tr>
                ) : (
                  leads.map((l) => (
                    <tr key={l.id} data-testid={`lead-row-${l.id}`} className="hover:bg-zinc-50">
                      <td className="p-4">{l.name}</td>
                      <td className="p-4">{l.email}</td>
                      <td className="p-4">{l.interest || "—"}</td>
                      <td className="p-4 text-zinc-500 font-mono text-xs">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "users" && (
          <div className="bg-white border border-zinc-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="text-left p-4 font-semibold">Nombre</th>
                  <th className="text-left p-4 font-semibold">Correo</th>
                  <th className="text-left p-4 font-semibold">Rol</th>
                  <th className="text-left p-4 font-semibold">Cursos</th>
                  <th className="text-left p-4 font-semibold">Registro</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-zinc-50">
                    <td className="p-4">{u.name}</td>
                    <td className="p-4">{u.email}</td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 ${u.role === "admin" ? "bg-[#FF3B30] text-white" : "bg-zinc-100"}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="p-4">{(u.purchased_courses || []).length}</td>
                    <td className="p-4 text-zinc-500 font-mono text-xs">{(u.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "txs" && (
          <div className="bg-white border border-zinc-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="text-left p-4 font-semibold">Curso</th>
                  <th className="text-left p-4 font-semibold">Comprador</th>
                  <th className="text-left p-4 font-semibold">Monto</th>
                  <th className="text-left p-4 font-semibold">Estado</th>
                  <th className="text-left p-4 font-semibold">Fecha</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {txs.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="p-8 text-center text-zinc-500">Sin transacciones.</td>
                  </tr>
                ) : (
                  txs.map((t) => (
                    <tr key={t.id} className="hover:bg-zinc-50">
                      <td className="p-4">{t.course_title}</td>
                      <td className="p-4">{t.user_email}</td>
                      <td className="p-4 font-semibold">${t.amount} {t.currency?.toUpperCase()}</td>
                      <td className="p-4">
                        <span className={`text-xs px-2 py-1 ${t.payment_status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100"}`}>
                          {t.payment_status}
                        </span>
                      </td>
                      <td className="p-4 text-zinc-500 font-mono text-xs">{(t.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
};

export default Admin;
